// React
import { useCallback, useState } from 'react'
import { HallType } from '@weblite/ts-action/hall'
// Material
import { Avatar, Box } from '@mui/material'
import { styled } from '@mui/material/styles'
// Common
import { PageWithAppBar } from '$common/page-with-app-bar'
import { SettingsSection } from '$common/settings-section'
import { AppBarBackTitleConfirm } from '$common/app-bar/app-bar-back-title-confirm'
import { MultilineInput, TextInput } from '$common/input'
import { CameraIcon } from '$common/icons/camera'
import { Label } from '$common/label'
import { WithEditableAvatar } from '$common/with-editable-avatar'
// Localization
import { t } from '$i18n'
// Helper
import { nothing } from '$func/utils'
import { createHall } from '$logic/hall'
import { hallConstraints } from '$logic/hall/constant'
import {
  closeCreateHall,
  openAddHallMembers,
  openDefineHallAccessibility,
} from '$logic/router'
import { getImageUrl } from '$func/imageUrl'
import { showProfilePictureMenu } from '$comp/modals/menus/profile-picture-menu'

const StyledAvatar = styled(Avatar)`
  width: 100px;
  height: 100px;
  margin: 0 auto;
`
const StyledPhotoCameraIcon = styled(CameraIcon)`
  width: 50px;
  height: 50px;
`

interface Props {
  hallType: HallType
}

export function CreateHall({ hallType }: Props) {
  // TODO: IMPLEMENT LOGIC FOR UPDATE
  const [imageFileId, setImageFileId] = useState<string>()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const onConfirmHandle = useCallback(async () => {
    if (!title) return
    // TODO: Show Loading

    const roomId = await createHall({
      name: title,
      type: hallType,
      description,
      image: imageFileId,
    })
    if (!roomId) return
    analite(hallType === 'CHANNEL' ? 'CREATE_CHANNEL' : 'CREATE_GROUP', {
      name: title,
      roomId,
    })

    if (hallType === 'GROUP') openAddHallMembers(roomId, true, true)
    else openDefineHallAccessibility(roomId, true)
  }, [imageFileId, title, description, hallType])

  const isCreatingChannel = hallType === 'CHANNEL'
  return (
    <PageWithAppBar
      appBar={
        <AppBarBackTitleConfirm
          title={
            isCreatingChannel
              ? t('createHall.createChannel')
              : t('createHall.createGroup')
          }
          confirmDisabled={!title}
          onBack={closeCreateHall}
          onConfirm={onConfirmHandle}
        />
      }
    >
      <SettingsSection
        sx={{
          padding: '14px 20px 20px',
        }}
      >
        <>
          <WithEditableAvatar
            onSelectClick={() =>
              showProfilePictureMenu({
                onSubmit: setImageFileId,
                showDelete: !!imageFileId,
              })
            }
          >
            <StyledAvatar src={getImageUrl(imageFileId)} onClick={nothing}>
              <StyledPhotoCameraIcon />
            </StyledAvatar>
          </WithEditableAvatar>

          <Box sx={{ marginTop: '23px' }}>
            <Label
              title={
                isCreatingChannel
                  ? t('profile.hall.channelName')
                  : t('profile.hall.groupName')
              }
              required
            />
            <TextInput
              onChange={setTitle}
              value={title}
              placeholder={
                isCreatingChannel
                  ? t('profile.hall.enterChannelName')
                  : t('profile.hall.enterGroupName')
              }
              maxLength={hallConstraints.name.maxLength}
            />

            <Label title={t('profile.hall.description')} />
            <MultilineInput
              maxLength={hallConstraints.description.maxLength}
              placeholder={
                isCreatingChannel
                  ? t('profile.hall.enterChannelDescription')
                  : t('profile.hall.enterGroupDescription')
              }
              value={description}
              onChange={setDescription}
              showMaxLength
            />
          </Box>
        </>
      </SettingsSection>
    </PageWithAppBar>
  )
}
